import type { ReactNode } from 'react';

/**
 * Wrap every match of the typed words in `<mark>`, without ever building HTML.
 *
 * A result list is scanned, not read: the point of the highlight is to show WHY
 * a row matched, which matters most on the subtitle, where the hit is often a
 * phone number or a vendor buried mid-string.
 *
 * It splits into React nodes rather than assembling a string for
 * `dangerouslySetInnerHTML`, because every value here is tenant data — a client
 * named with a stray `<` would otherwise be an injection point on a surface that
 * shows nine tables at once.
 *
 * Longest term first: a short word that is a prefix of a longer one would
 * otherwise split the longer match in two ("in" cutting "invoice" into three
 * fragments).
 */
const REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;

export function highlightTerms(text: string, query: string): ReactNode {
  const terms = query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => term.replace(REGEX_SPECIAL, '\\$&'))
    .sort((a, b) => b.length - a.length);
  if (!terms.length) return text;

  const pattern = new RegExp(`(${terms.join('|')})`, 'gi');
  // `split` on a capturing group yields [plain, match, plain, match, …], so the
  // odd positions are the matches and no second pass over the string is needed.
  // The key is each piece's OFFSET in the source string, which is its real
  // identity: one split of one string, so no two pieces can ever share a start.
  let offset = 0;
  return text.split(pattern).map((piece, position) => {
    const start = offset;
    offset += piece.length;
    if (position % 2 === 0) return piece;
    return (
      <mark key={start} className="bg-transparent font-semibold text-primary-ink">
        {piece}
      </mark>
    );
  });
}
